import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";

import { getBranch } from "../../utils/app.utils";

import ZoneApi, { IStallLookup, IZoneLookup } from "../../api/zone/zone.api";
import StallTypeApi from "../../api/setting/stall/stall.api";
import { StallReclaimParams, StallReclaimStatus } from "../../api/stall-reclaim/stall-reclaim.api";
import {
  STALL_RECLAIM_FILTER_ALL,
  STALL_RECLAIM_STATUS_ALL,
} from "../../constants/stallReclaim";
import { stallLabel } from "../../utils/stall.utils";

/** ค่าของ dropdown ที่ยังไม่กรอง — sentinel ที่ไม่ใช่ '' เพราะ MUI Select ไม่เรียก renderValue ตอนค่าว่าง */
type FilterValue = number | typeof STALL_RECLAIM_FILTER_ALL;

/** ประเภทแผงค้าที่ใช้ทำ dropdown — เส้น stall-type คืน field มาเยอะกว่านี้ แต่ใช้แค่ 2 ตัว */
export type StallTypeOption = {
  stallTypeId: number;
  stallTypeNameTh: string | null;
};

/**
 * แปลง response ของโซนให้เป็นรูป lookup รูปเดียว
 *
 * รับได้ทั้ง array ดิบ (เส้น zone เดิม) และแบบห่อ { data } (โมดูลใหม่) เพราะ envelope
 * ของ zone/lite ยังไม่ยืนยันกับ backend และ findAll (ตัว fallback) คืน array ดิบแน่นอน
 * ตัดแผงที่ status = DELETED ออกด้วย เพราะ findAll คืนแผงที่ถูกลบมาให้ด้วย
 */
const toZoneLookup = (payload: any): IZoneLookup[] => {
  const rows: any[] = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : [];

  return rows
    .map((zone: any) => ({
      zoneId: Number(zone?.zoneId),
      zoneName: zone?.zoneName ?? null,
      stall: (Array.isArray(zone?.stall) ? zone.stall : [])
        .filter((stall: any) => stall?.status !== "DELETED")
        .map((stall: any) => ({
          stallId: Number(stall?.stallId),
          stallName: stall?.stallName ?? null,
          stallNumber: stall?.stallNumber ?? null,
        }))
        .filter((stall: IStallLookup) => Number.isFinite(stall.stallId)),
    }))
    .filter((zone) => Number.isFinite(zone.zoneId));
};

/**
 * state ของแถบตัวกรองหน้าเรียกคืนแผงค้า + ตัวเลือกโซน/แผงค้า/ประเภทแผงค้า
 *
 * ต่างจาก useDepositRefundFilter ที่ใช้ endpoint ของโมดูลตัวเอง (/filter/zone, /filter/stall)
 * เมนูนี้ backend ให้ใช้ master ที่มีอยู่แล้ว: zone/lite?view=lookup (โซน + แผงซ้อนในก้อนเดียว)
 * และ stall-type จึงต้อง derive ตัวเลือกแผงค้าจากโซนที่เลือก ไม่ได้เก็บเป็น state แยก
 * (เก็บแยกแล้วมีโอกาสหลุด sync กับโซน)
 *
 * ตั้งใจให้ hook นี้ "ไม่ยิง API ของตาราง" — คืนแค่ state กับ params ที่ประกอบแล้ว
 * ให้หน้าเพจเขียน effect โหลดข้อมูลเอง เพื่อให้เห็นชัดว่าหน้านั้นเรียก endpoint ไหน
 */
export const useStallReclaimFilter = () => {
  /**
   * สาขาที่เลือกไว้ตอน login — ต้องส่งทุกเส้น ไม่งั้นเห็นคำสั่งข้ามสาขา
   * (backend ไม่ได้ scope จาก token เพราะ user 1 คนไม่ผูกกับสาขาเดียว)
   */
  const branchId = useMemo(() => {
    const value = Number(getBranch());
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }, []);

  /** ค่าที่พิมพ์อยู่ในช่อง — แยกจาก search เพื่อ debounce ไม่ให้ยิง API ทุก keystroke */
  const [searchInput, setSearchInput] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [status, setStatus] = useState<string>(STALL_RECLAIM_STATUS_ALL);
  const [zoneId, setZoneId] = useState<FilterValue>(STALL_RECLAIM_FILTER_ALL);
  const [stallId, setStallId] = useState<FilterValue>(STALL_RECLAIM_FILTER_ALL);
  const [stallTypeId, setStallTypeId] = useState<FilterValue>(STALL_RECLAIM_FILTER_ALL);
  const [dateStart, setDateStart] = useState<string>("");
  const [dateEnd, setDateEnd] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(10);

  const [zones, setZones] = useState<IZoneLookup[]>([]);
  const [stallTypes, setStallTypes] = useState<StallTypeOption[]>([]);

  useEffect(() => {
    /** กัน setState หลัง unmount ตอนผู้ใช้ออกจากหน้าก่อน response กลับมา */
    let isMounted = true;

    /**
     * zone/lite เป็นเส้นที่เบาที่สุด แต่มีบันทึกในโปรเจกต์ว่า backend เคยเตรียมถอด route นี้ออก
     * (ดู pages/store/market-map/display/zone-plan.md) ถ้ายิงไม่ได้จึงถอยไปใช้ GET /zone
     * ที่ทุกหน้าใช้อยู่ — payload อ้วนกว่าแต่มี stall ซ้อนมาให้เหมือนกัน
     */
    const loadZones = async () => {
      try {
        const res = await ZoneApi.lookup(branchId);
        const zoneRows = toZoneLookup(res?.data);
        if (zoneRows.length > 0) return zoneRows;
      } catch (error) {
        // ตกไป fallback ด้านล่าง ไม่ต้องเด้ง error ให้ผู้ใช้ (ตัวกรองไม่ใช่ข้อมูลหลัก)
      }

      const fallback = await ZoneApi.findAll({
        page: 1,
        pageLimit: 1000,
        branchId,
        // 0 = ไม่ต้องคำนวณค่าน้ำค่าไฟมาด้วย เร็วกว่ามากและ dropdown ไม่ได้ใช้
        skipcalculation: 0,
      });
      return toZoneLookup(fallback?.data);
    };

    const loadStallTypes = async () => {
      // เส้นนี้คืน array ดิบใน res.data ไม่ใช่ res.data.data แบบโมดูลใหม่
      const res = await StallTypeApi.findAll({ page: 1, pageLimit: 100, status: "ACTIVE" });
      const rows: any[] = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
      return rows
        .map((row: any) => ({
          stallTypeId: Number(row?.stallTypeId),
          stallTypeNameTh: row?.stallTypeNameTh ?? null,
        }))
        .filter((row: StallTypeOption) => Number.isFinite(row.stallTypeId));
    };

    const loadFilterMeta = async () => {
      // ยิงขนานได้ ไม่ต้องรอกัน · allSettled เพื่อให้ตัวที่พลาดไม่ล้มอีกตัว
      const [zoneResult, stallTypeResult] = await Promise.allSettled([
        loadZones(),
        loadStallTypes(),
      ]);
      if (!isMounted) return;
      setZones(zoneResult.status === "fulfilled" ? zoneResult.value : []);
      setStallTypes(stallTypeResult.status === "fulfilled" ? stallTypeResult.value : []);
    };

    loadFilterMeta();
    return () => {
      isMounted = false;
    };
  }, [branchId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  /** ยังไม่เลือกโซน = รวมแผงของทุกโซน (เลือกแผงตรงๆ ได้เลยไม่ต้องไล่หาโซนก่อน) */
  const stallOptions = useMemo<IStallLookup[]>(() => {
    if (zoneId === STALL_RECLAIM_FILTER_ALL) {
      return zones.reduce<IStallLookup[]>((all, zone) => all.concat(zone.stall || []), []);
    }
    return zones.find((zone) => zone.zoneId === zoneId)?.stall || [];
  }, [zones, zoneId]);

  const handleChangeStatus = (value: string) => {
    setStatus(value);
    setPage(1);
  };

  /** เปลี่ยนโซนต้องล้างแผงค้าเดิม ไม่งั้นค้างแผงของโซนอื่นแล้วตารางว่างแบบหาสาเหตุไม่เจอ */
  const handleChangeZone = (value: FilterValue) => {
    setZoneId(value);
    setStallId(STALL_RECLAIM_FILTER_ALL);
    setPage(1);
  };

  const handleChangeStall = (value: FilterValue) => {
    setStallId(value);
    setPage(1);
  };

  const handleChangeStallType = (value: FilterValue) => {
    setStallTypeId(value);
    setPage(1);
  };

  const handleChangeDateStart = (value: any) => {
    // InputDatePicker โชว์เป็น พ.ศ. แต่ค่าที่ส่ง backend ต้องเป็น ค.ศ. ISO
    setDateStart(value ? moment(value).format("YYYY-MM-DD") : "");
    setPage(1);
  };

  const handleChangeDateEnd = (value: any) => {
    setDateEnd(value ? moment(value).format("YYYY-MM-DD") : "");
    setPage(1);
  };

  const handleChangePageLimit = (value: number) => {
    setPageLimit(value);
    setPage(1);
  };

  /**
   * ตัวกรองที่ใช้ร่วมกันระหว่างตารางกับ summary
   * ไม่รวม status/page/limit เพื่อให้ summary นับได้ครบทุกสถานะ
   */
  const buildBaseParams = useCallback((): StallReclaimParams => {
    let condition: StallReclaimParams = {};
    if (branchId) condition = { ...condition, branchId };
    if (search) condition = { ...condition, keyword: search };
    if (zoneId !== STALL_RECLAIM_FILTER_ALL) condition = { ...condition, zoneId };
    if (stallId !== STALL_RECLAIM_FILTER_ALL) condition = { ...condition, stallId };
    if (stallTypeId !== STALL_RECLAIM_FILTER_ALL) condition = { ...condition, stallTypeId };
    if (dateStart) condition = { ...condition, date_from: dateStart };
    if (dateEnd) condition = { ...condition, date_to: dateEnd };
    return condition;
  }, [branchId, search, zoneId, stallId, stallTypeId, dateStart, dateEnd]);

  /** params เต็มของตาราง = ตัวกรองร่วม + สถานะ + หน้า */
  const buildTableParams = useCallback((): StallReclaimParams => {
    let condition: StallReclaimParams = { ...buildBaseParams(), page, limit: pageLimit };
    if (status !== STALL_RECLAIM_STATUS_ALL) {
      condition = { ...condition, status: status as StallReclaimStatus };
    }
    return condition;
  }, [buildBaseParams, page, pageLimit, status]);

  /** ข้อความในลิสต์ตัวเลือกแผงค้า — ชื่อร้าน + เลขแผง ให้แยกแผงที่ชื่อซ้ำกันได้ */
  const stallOptionLabel = (stall: IStallLookup) => stallLabel(stall);

  return {
    searchInput,
    setSearchInput,
    status,
    handleChangeStatus,
    zoneId,
    handleChangeZone,
    stallId,
    handleChangeStall,
    stallOptionLabel,
    stallTypeId,
    handleChangeStallType,
    stallTypes,
    dateStart,
    handleChangeDateStart,
    dateEnd,
    handleChangeDateEnd,
    page,
    setPage,
    pageLimit,
    handleChangePageLimit,
    zones,
    /** ลิสต์ที่กรองตามโซนที่เลือกแล้ว — หน้าเพจใช้ตัวนี้ทำ option */
    stallOptions,
    buildBaseParams,
    buildTableParams,
  };
};
